<template>
    <div :class="[stop.noArret.toString() === noStop ? 'stop-container-active' : '']" class="stop-item-container">
        <RouterLink
            :disabled="stop.noArret.toString() === noStop"
            :class="[stop.noArret.toString() === noStop ? 'is-disabled' : '']"
            :to="{
                name: vRoute.name,
                params: {
                    route: vRoute.params.route,
                    stop: `${stop.noArret}-${slugify(stop.nom)}`,
                    direction: slugify(direction),
                    date: selectedDate,
                },
            }"
        >
            <span v-if="hour.arretNonDesservi" class="visuallyhidden">{{ t('unserved_stop') }}</span>
            <div class="stop-infos" :class="[stop.noArret.toString() === noStop ? 'is-active' : '']">
                <div class="stop-name-container">
                    <div>
                        <span v-if="stop.noArret.toString() === noStop" class="visuallyhidden">{{
                            t('current_stop')
                        }}</span>
                        <span class="stop-name">{{ stop.noArret }} - {{ stop.nom }}</span
                        ><span class="no-wrap"
                            >&nbsp;
                            <IconDropdownArrow
                                v-if="stop.noArret.toString() !== noStop"
                                class="chevron-icon"
                                width="9"
                                height="16"
                        /></span>
                    </div>
                    <small class="stop-description">{{ stop.description }}</small>
                </div>
                <div class="stop-hour-container">
                    <img
                        v-if="route.accessible && stop.accessible"
                        class="accessible-icon"
                        src="@@/img/accessible.svg"
                        width="18"
                        height="18"
                    />
                    <span v-if="route.accessible && stop.accessible" class="visuallyhidden">{{
                        $t('a11y.accessible.accessible_stop')
                    }}</span>
                    <span v-else class="visuallyhidden">{{ $t('a11y.accessible.not_accessible_stop') }}</span>
                    <span v-if="hour" class="visuallyhidden">{{ getA11yHour(hour.depart) }}</span>
                    <span v-if="hour" :class="{ 'passed-hour': isPassedHour }" class="hour">
                        {{ format(hour.depart, locale === 'fr' ? "HH'h'mm" : 'HH:mm') }}
                        <span class="visuallyhidden">{{ t('passed_hour') }}</span>
                    </span>
                </div>
            </div>
        </RouterLink>
        <AccordionList v-if="stop.noArret.toString() === noStop && transfers.length != 0" class="transfers">
            <AccordionItem ref="transfer" :name="`transfers-${stop.noArret}`" class="transfers-accordion-item">
                <template #title="{ isOpen }">
                    <div class="transfers-button">
                        <IconTransfer class="transfers-icon" width="15" height="9" />
                        <span class="button-text">{{ !isOpen ? t('transfer_open') : t('transfer_close') }}</span>
                        <IconDropdownArrow class="chevron-icon" width="9" height="16" />
                    </div>
                </template>
                <template #content>
                    <ul class="transfer-list no-bullet">
                        <li v-for="(transfer, k) in transfers" :key="k" class="transfer-item">
                            <RouterLink
                                class="flex-container align-top"
                                :to="{
                                    name: vRoute.name,
                                    params: {
                                        route: transfer.noParcours,
                                        stop: `${stop.noArret}-${slugify(stop.nom)}`,
                                        direction: slugify(transfer.descriptionDirection),
                                        date: selectedDate,
                                    },
                                }"
                            >
                                <span class="route-number" :class="getServiceClass(transfer.codeTypeService)">
                                    {{ transfer.noParcours }}
                                </span>
                                <span class="route-direction">{{ transfer.descriptionDirection }}</span>
                            </RouterLink>
                        </li>
                    </ul>
                </template>
            </AccordionItem>
        </AccordionList>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { RouterLink, useRoute } from 'vue-router';

import { format } from '@@/js/utils/dateFns';
import { useSchedulesStore } from '@@/js/stores/schedules';
import slugify from '@@/js/utils/slugify';
import AccordionList from '@/shared/AccordionList';
import AccordionItem from '@/shared/AccordionItem';

import IconDropdownArrow from '@@/icons/dropdown-arrow.svg';
import IconTransfer from '@@/icons/transfer.svg';

defineProps({
    isPassedHour: {
        type: Boolean,
        default: false,
    },
    noRoute: {
        type: String,
        default: '',
    },
    noStop: {
        type: String,
        default: '',
    },
    direction: {
        type: String,
        default: '',
    },
    selectedDate: {
        type: String,
        default: '',
    },
    route: {
        type: Object,
        default: () => ({}),
    },
    stop: {
        type: Object,
        default: () => ({}),
    },
    hour: {
        type: Object,
        default: () => ({}),
    },
    transfers: {
        type: Array,
        default: () => [],
    },
});

const transfer = ref();
defineExpose({ transfer });

const { t, locale } = useI18n({
    messages: {
        fr: {
            current_stop: 'Arrêt courant',
            hour: 'heure',
            transfer_open: 'Voir les correspondances',
            transfer_close: 'Cacher les correspondances',
            passed_hour: 'Heure passée',
            unserved_stop: 'Arrêt non desservi',
        },
        en: {
            current_stop: 'Current stop',
            hour: ' ',
            transfer_open: 'View connection',
            transfer_close: 'Hide connection',
            passed_hour: 'Passed hour',
            unserved_stop: 'Unserved stop',
        },
    },
});

const vRoute = useRoute();

const { getServiceType } = useSchedulesStore();

function getServiceClass(code) {
    const service = getServiceType(code);
    return service?.class || '';
}

function getA11yHour(hour) {
    const hourString = t('hour');
    return format(hour, `HH$'${hourString}'mm`);
}
</script>

<style lang="scss" scoped>
.stop-item-container {
    @include rem-typo(16px);
    a {
        display: block;
        text-decoration: none;
    }
}
.is-disabled {
    cursor: default;
}
.is-active {
    padding: 16px;
    border-radius: 15px;
    background-color: $color-blue-60;
    @include breakpoint(medium) {
        margin: 0;
    }
}
.stop-infos {
    display: flex;
    justify-content: space-between;
}
.stop-name-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    .chevron-icon {
        transform: rotate(-90deg);
    }
}
.stop-name {
    color: $color-blue-60;
    .is-active & {
        font-weight: 700;
        color: $color-white;
    }
    @include breakpoint(medium) {
        @include rem-typo(18px);
    }
}
.stop-description {
    @include rem-typo(12px);
    display: block;
    color: $color-black-50;
    .is-active & {
        color: $color-white;
    }
}
.accessible-icon {
    margin-right: 6px;
    margin-left: 10px;
}
.hour {
    @include rem-typo(16px);
    font-weight: 700;
    color: $color-black-100;
    .is-active & {
        color: $color-white;
    }
    .is-unserved & {
        text-decoration: line-through;
    }
    @include breakpoint(medium) {
        @include rem-typo(18px);
    }
}
.passed-hour {
    color: $color-black-20;
}
.stop-container-active {
    margin: 0 -16px;
}
.transfers {
    margin-bottom: -5px;
    @include breakpoint(medium) {
        margin-bottom: -13px;
    }
}
.transfers-accordion-item {
    :deep(.accordion-button) {
        background-color: transparent;
        border-radius: 0;
        &:hover,
        &:focus {
            .button-text {
                text-decoration: none;
            }
        }
    }
}
.transfers-button {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px;
    font-size: 14px;
    font-weight: 400;
    text-align: left;
    color: $color-blue-60;
    .is-active & {
        color: $color-white;
    }
    .button-text {
        text-decoration: underline;
    }
    .chevron-icon {
        text-decoration: none;
        transition: all 300ms ease;
        .is-open & {
            transform: rotate(180deg);
        }
    }
}
.transfers-icon {
    fill: $color-black-50;
}
.transfer-list {
    @include rem-typo(14px);
    margin: 0 -8px -16px;
    padding: 8px 22px 16px;
}
.transfer-item {
    margin-bottom: 8px;
    &:last-child {
        margin-bottom: 0;
    }
    a {
        color: $color-blue-60;
    }
}
.route-number {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 33px;
    height: 24px;
    margin-right: 8px;
    font-weight: 700;
    text-align: center;
    color: $color-white;
    border-radius: 3px;
    @include colorize {
        background-color: get-color-variation('base');
    }
}
.route-direction {
    text-decoration: underline;
}
.stop-hour-container {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-shrink: 0;
}
</style>
